import * as z from "zod";

const ZEILE = z.string().max(256);
const NAME = z.string().min(1).max(48);
const INDEX = z.number().int().min(0).max(53);
const ITEM_ID = /^[a-z0-9_.-]+:[a-z0-9_./-]+$/;
const HAT_PLATZHALTER = /\{[a-z0-9_]+\}/;

export const SymbolSchema = z
  .string()
  .max(128)
  .refine((s) => ITEM_ID.test(s) || HAT_PLATZHALTER.test(s), { message: "Ungültige Item-ID." });

export const SlotFelderSchema = z.object({
  symbol: SymbolSchema,
  anzahl: z.union([z.number().int().min(1).max(64), z.string().max(64)]).optional(),
  name: ZEILE.optional(),
  beschreibung: z.array(ZEILE).max(30).optional(),
  aktion: z.string().max(64).optional(),
  wert: z.string().max(256).optional(),
  bestaetigung: ZEILE.optional(),
  leuchten: z.boolean().optional(),
  bedrockBild: z.string().max(128).optional(),
});
export type SlotFelder = z.infer<typeof SlotFelderSchema>;

export const SlotUeberschreibungSchema = SlotFelderSchema.partial().extend({ leer: z.literal(true).optional() });
export type SlotUeberschreibung = z.infer<typeof SlotUeberschreibungSchema>;

export const SlotSchema = SlotFelderSchema.extend({
  zustaende: z.record(z.string(), SlotUeberschreibungSchema).optional(),
});
export type Slot = z.infer<typeof SlotSchema>;

export const SlotAenderungSchema = z.object({
  symbol: SymbolSchema.optional(),
  anzahl: z.union([z.number().int().min(1).max(64), z.string().max(64)]).nullable().optional(),
  name: ZEILE.nullable().optional(),
  beschreibung: z.array(ZEILE).max(30).nullable().optional(),
  aktion: z.string().max(64).nullable().optional(),
  wert: z.string().max(256).nullable().optional(),
  bestaetigung: ZEILE.nullable().optional(),
  leuchten: z.boolean().nullable().optional(),
  bedrockBild: z.string().max(128).nullable().optional(),
  zustaende: z.record(z.string(), SlotUeberschreibungSchema).nullable().optional(),
});
export type SlotAenderung = z.infer<typeof SlotAenderungSchema>;

export const BlaetterSlotSchema = SlotFelderSchema.partial().extend({ slot: INDEX });
export type BlaetterSlot = z.infer<typeof BlaetterSlotSchema>;

export const ListenbereichSchema = z.object({
  name: NAME,
  quelle: NAME,
  slots: z.array(INDEX).max(54),
  vorlage: SlotFelderSchema,
  zustaende: z.record(z.string(), SlotFelderSchema.partial()).optional(),
  blaettern: z
    .object({ zurueck: BlaetterSlotSchema.optional(), weiter: BlaetterSlotSchema.optional(), info: BlaetterSlotSchema.optional() })
    .optional(),
  leer: SlotFelderSchema.optional(),
});
export type Listenbereich = z.infer<typeof ListenbereichSchema>;

export const FormSchema = z.discriminatedUnion("art", [
  z.object({ art: z.literal("truhe"), reihen: z.literal([1, 2, 3, 4, 5, 6]) }),
  z.object({ art: z.literal("trichter") }),
  z.object({ art: z.literal("spender") }),
]);
export type Form = z.infer<typeof FormSchema>;

export const UiDatenSchema = z.object({
  titel: z.string().max(128),
  form: FormSchema,
  rahmen: z.object({ symbol: SymbolSchema }),
  quelle: NAME.optional(),
  text: z.array(ZEILE).max(30),
  slots: z.record(z.string().regex(/^\d+$/), SlotSchema),
  listen: z.array(ListenbereichSchema),
});
export type UiDaten = z.infer<typeof UiDatenSchema>;

export const StatusSchema = z.enum(["idee", "in_arbeit", "bereit", "freigegeben", "im_spiel"]);
export type Status = z.infer<typeof StatusSchema>;

export const RolleSchema = z.enum(["eigentuemer", "admin", "bearbeiter", "leser"]);
export type Rolle = z.infer<typeof RolleSchema>;

export const WerteSchema = z.record(z.string(), z.union([z.string(), z.array(z.string())]));
export type Werte = z.infer<typeof WerteSchema>;

export const QuellendatenSchema = z.object({
  werte: WerteSchema,
  zustand: z.string().optional(),
  eintraege: z.array(z.object({ werte: WerteSchema, zustand: z.string().optional() })),
});
export type Quellendaten = z.infer<typeof QuellendatenSchema>;

export const PlatzhalterSchema = z.object({
  name: z.string().regex(/^[a-z0-9_]+$/),
  beschreibung: z.string().max(256).optional(),
  beispiel: z.union([z.string(), z.array(z.string())]),
  mehrzeilig: z.boolean().optional(),
});
export type Platzhalter = z.infer<typeof PlatzhalterSchema>;

export const KatalogAktionSchema = z.object({
  schluessel: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  beschreibung: z.string().max(256).optional(),
  argument: z.union([z.enum(["keins", "frei", "ui"]), z.object({ werte: z.array(z.string()) })]),
});
export type KatalogAktion = z.infer<typeof KatalogAktionSchema>;

export const KatalogQuelleSchema = z.object({
  schluessel: NAME,
  name: z.string().min(1).max(64),
  menueWerte: z.array(PlatzhalterSchema),
  menueZustaende: z.array(z.string()),
  eintragWerte: z.array(PlatzhalterSchema),
  eintragZustaende: z.array(z.string()),
  beispiel: QuellendatenSchema,
});
export type KatalogQuelle = z.infer<typeof KatalogQuelleSchema>;

export const KatalogSchema = z.object({
  aktionen: z.array(KatalogAktionSchema),
  quellen: z.array(KatalogQuelleSchema),
  farbrollen: z.record(z.string(), z.string().regex(/^#[0-9A-Fa-f]{6}$/)),
  bedrockBilder: z.record(z.string(), z.string()),
  darstellung: z.object({ titelVorlage: z.string().max(256), kursivAus: z.boolean() }),
});
export type Katalog = z.infer<typeof KatalogSchema>;

export const OperationSchema = z.discriminatedUnion("art", [
  z.object({ art: z.literal("slot_setzen"), index: INDEX, felder: SlotAenderungSchema }),
  z.object({ art: z.literal("slots_leeren"), indizes: z.array(INDEX) }),
  z.object({
    art: z.literal("slots_verschieben"),
    paare: z.array(z.object({ von: INDEX, nach: INDEX })),
    kopieren: z.boolean(),
  }),
  z.object({
    art: z.literal("ui_setzen"),
    felder: z.object({
      titel: UiDatenSchema.shape.titel.optional(),
      form: FormSchema.optional(),
      rahmen: UiDatenSchema.shape.rahmen.optional(),
      quelle: NAME.nullable().optional(),
      text: UiDatenSchema.shape.text.optional(),
    }),
  }),
  z.object({ art: z.literal("listenbereich_setzen"), bereich: ListenbereichSchema, position: z.number().int().min(0).optional() }),
  z.object({ art: z.literal("listenbereich_entfernen"), name: NAME }),
  z.object({ art: z.literal("alles_ersetzen"), daten: UiDatenSchema }),
]);
export type Operation = z.infer<typeof OperationSchema>;

export const STANDARD_RAHMEN = "minecraft:gray_stained_glass_pane";

export const STANDARD_KATALOG: Katalog = {
  aktionen: [{ schluessel: "menue.oeffnen", name: "Menü öffnen", beschreibung: "Öffnet eine andere UI.", argument: "ui" }],
  quellen: [],
  farbrollen: {},
  bedrockBilder: {},
  darstellung: { titelVorlage: "{titel}", kursivAus: false },
};

export function neueUiDaten(titel: string): UiDaten {
  return { titel, form: { art: "truhe", reihen: 3 }, rahmen: { symbol: STANDARD_RAHMEN }, text: [], slots: {}, listen: [] };
}
